import React, { FC, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  AvatarGroup,
  AvatarSize,
  AvatarGroupSpacing,
  Icon,
  IconSize,
  useSideSheetState,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import { CurrentWeek } from '@bechtel/client-dashboard-common';
import { DashboardHeaderRowStyled, DashboardHeaderHoverStyled } from './styled';
import { Trans } from '@lingui/macro';
import { CrewMember, PersonaType } from '@bechtel/types';
import { useName, ErrorRetry } from '@bechtel/client-common';
import { get, map } from 'lodash';

import { SupportingForemanList } from '~/supporting-foreman-list';
import { useAssociatedCrewMember } from '~/hooks/use-associated-crew-member';

interface DashboardHeaderProps {
  crewMember?: CrewMember;
}

const GetCrewMembers = (crewMembers: CrewMember[]) => {
  const { getFullName } = useName();
  return map(crewMembers, (crewMember: CrewMember) => {
    return {
      src: crewMember?.imageUrl ? crewMember.imageUrl : '',
      alt: getFullName(
        crewMember?.firstName ? crewMember.firstName : ' ',
        crewMember?.lastName ? crewMember.lastName : ' '
      ),
    };
  });
};

export const DashboardHeader: FC<DashboardHeaderProps> = ({ crewMember }) => {
  const { visible: visibleSidesheet, open: openSidesheet, close: closeSidesheet } = useSideSheetState(false);

  const [getAssociatedCrewMember, { data, error, loading, refetch }] = useAssociatedCrewMember(true);

  useEffect(() => {
    if (crewMember?.persona !== PersonaType.FIELD_WELD_ENGINEER) return;
    getAssociatedCrewMember({
      variables: {
        input: {
          crewMemberId: crewMember?.id,
        },
      },
    });
  }, [crewMember]);

  const crewMembers = useMemo(() => {
    return get(data, 'loadAssociatedCrewMembers.associatedCrewMembers', []);
  }, [data]);

  return (
    <>
      <DashboardHeaderRowStyled data-testid="dashboardHeader-container">
        <Box mx={{ sm: '600' }} alignItems={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
          <CurrentWeek></CurrentWeek>
          {crewMember && crewMember.persona === PersonaType.FIELD_WELD_ENGINEER && (
            <DashboardHeaderHoverStyled data-testid="dashboardHeader-hover">
              {crewMembers && crewMembers?.length > 0 && (
                <Box mt={{ sm: '400' }} color="bechtel.blue.700" family="graphik" size="300">
                  <Trans>Supporting FMs</Trans>
                </Box>
              )}
              {crewMembers && crewMembers?.length == 0 && (
                <Box data-testid="no-supporting-foreman">
                  <Text color="bechtel.blue.700" family="graphik" size="300">
                    <Trans>Add Supporting FM</Trans>
                  </Text>
                </Box>
              )}
              <Box mx={{ sm: '400' }} data-testid="supprting-foreman-list">
                {!loading && !error && crewMembers && crewMembers?.length > 0 && (
                  <AvatarGroup
                    maxCount={5}
                    data={GetCrewMembers(crewMembers)}
                    size={AvatarSize.MEDIUM}
                    spacing={AvatarGroupSpacing.SMALL}
                    css={`
                      background-color: ${({ theme }) => theme.colors.grey[200]};
                      color: ${({ theme }) => theme.colors.grey[600]};
                    `}
                    data-testid="supported-fms"
                  />
                )}
              </Box>
              <Icon
                color="bechtel.blue.700"
                icon="edit"
                size={IconSize.MEDIUM}
                style={{ cursor: 'pointer' }}
                onClick={openSidesheet}
                data-testid="edit-icon"
              />

              {loading && !error && crewMembers.length === 0 && (
                <Box flexGrow={{ sm: 1 }} data-testid="loading-supporting-foreman">
                  <Spinner size={SpinnerSize.SMALL} />
                </Box>
              )}
              {!loading && error && (
                <ErrorRetry refetch={refetch}>
                  <Trans>There was a problem loading foreman list</Trans>
                </ErrorRetry>
              )}
            </DashboardHeaderHoverStyled>
          )}
        </Box>
      </DashboardHeaderRowStyled>
      {visibleSidesheet && (
        <SupportingForemanList crewMember={crewMember} crewMembers={crewMembers} close={closeSidesheet} />
      )}
    </>
  );
};
